import React from "react";
import i18n from "../../../i18n";

import { Panel, Eyebrow, FieldRow } from "./Panel";
import { convertTime } from "../../../helpers/helpers";
import { Trans, useTranslation } from "react-i18next";

/**
 * The read-only detail panels: Route, Shipment detail, Invoice identity and
 * the payment slip. Field paths and fallbacks match ShipmentSummary.jsx,
 * ShipmentDetails.jsx, Header.jsx and Footer.jsx exactly.
 */

/* ── Route ─────────────────────────────────────────────────────────────── */

const addressRows = (data, prefix) => [
  [i18n.t("admin:invoice.address.reference"), prefix === "f" ? data?.f_reference : data?.referance_code],
  [i18n.t("admin:invoice.address.name"), data?.[`${prefix}attension`]],
  [i18n.t("admin:invoice.address.company"), data?.[`${prefix}company`]],
  [i18n.t("admin:invoice.address.address"), data?.[`${prefix}address`]],
  [i18n.t("admin:invoice.address.country"), data?.[`${prefix}country`]],
  [i18n.t("admin:invoice.address.state"), data?.[`${prefix}state`]],
  [i18n.t("admin:invoice.address.city"), data?.[`${prefix}city`]],
  [i18n.t("admin:invoice.address.zip"), data?.[`${prefix}zip`]],
  [i18n.t("admin:invoice.address.phone"), data?.[`${prefix}phone`]],
  [i18n.t("admin:invoice.address.email"), data?.[`${prefix}email`]],
];

const ServiceChip = ({ label, on }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-manrope text-[10px] font-semibold ${
      on
        ? "border-state-success-border bg-state-success-bg text-state-success-text"
        : "border-ui-border bg-ui-surface-sunken text-ui-text-muted"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        on ? "bg-state-success-dot" : "bg-ui-border-strong"
      }`}
    />
    {label}
  </span>
);

export const RoutePanel = ({ data }) => {
  const { t } = useTranslation("admin");
  // Insurance Type / Amount only appear for shipment-level insurance, same
  // condition as ShipmentSummary.jsx.
  const perShipmentInsurance =
    data?.insurance && data?.insurance_type === "Shipment";

  return (
    <Panel
      title={t("invoice.route")}
      hint={[data?.fcity, data?.tcity].filter(Boolean).join(" → ")}
    >
      <div className="grid gap-px bg-ui-border [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        {[
          [t("invoice.shipFrom"), "f"],
          [t("invoice.shipTo"), "t"],
        ].map(([title, prefix]) => (
          <div key={prefix} className="min-w-0 bg-ui-surface px-2.5 py-2">
            <Eyebrow size="sm" className="mb-1.5">{title}</Eyebrow>
            {addressRows(data, prefix).map(([label, value]) => (
              <FieldRow key={label} label={label} value={value} />
            ))}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-t border-ui-border px-2.5 py-2">
        <Eyebrow>{t("invoice.additionalServices")}</Eyebrow>
        <ServiceChip label={t("invoice.signature")} on={!!data?.signature} />
        <ServiceChip label={t("invoice.residentialDelivery")} on={!!data?.tresidential} />
        <ServiceChip label={t("invoice.insurance")} on={!!data?.insurance} />
        {perShipmentInsurance && (
          <ServiceChip
            label={t("invoice.perInsurance", { type: data?.insurance_type, amount: data?.insurance_amount ?? "0" })}
            on
          />
        )}
        <ServiceChip label={t("invoice.tailgatePickup")} on={!!data?.tailgate_pickup} />
        <ServiceChip label={t("invoice.tailgateDelivery")} on={!!data?.tailgate_delivery} />
      </div>
    </Panel>
  );
};

/* ── Invoice & shipment detail ─────────────────────────────────────────── */

export const InvoiceDetailPanel = ({ data }) => {
  const { t } = useTranslation("admin");
  const pickupDetails = data?.PickupDate
    ? [
        data.PickupDate.slice(0, 10),
        data.readyopentime && data.readyopentime.slice(0, 5),
        data.readyopentime && data.readyclosetime ? "-" : null,
        data.readyclosetime && data.readyclosetime.slice(0, 5),
      ]
        .filter(Boolean)
        .join(" ")
    : null;

  // Production compares loosely here (ShipmentDetails.jsx) and by_card arrives
  // as both 1 and "1". A strict compare would silently flip a card payment to
  // "By Balance" on every shipment where the API returns the number.
  // eslint-disable-next-line eqeqeq
  const paymentLabel = data?.by_card == "1" ? t("invoice.byCard") : t("invoice.byBalance");

  return (
    <Panel title={t("invoice.invoiceShipmentDetail")}>
      <div className="grid gap-px bg-ui-border [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        <div className="min-w-0 bg-ui-surface px-2.5 py-2">
          <FieldRow label={t("invoice.invoiceNumber")} value={data?.invoice_id} />
          <FieldRow label={t("invoice.adminId")} value={data?.admin_invoice_id} />
          <FieldRow
            label={t("invoice.invoiceDate")}
            value={data?.created_at ? convertTime(data.created_at) : null}
          />
          <FieldRow
            label={t("invoice.customerId")}
            value={data?.created_by?.id || data?.user?.id}
          />
          <FieldRow
            label={t("invoice.createdBy")}
            value={
              data?.created_by?.username?.trim() ||
              data?.user?.username?.trim()
            }
          />
          <FieldRow label={t("invoice.boltTracking")} value={data?.tracking_id} />
          <FieldRow
            label={t("invoice.carrierTracking")}
            value={data?.shipment_tracking_id}
          />
        </div>
        <div className="min-w-0 bg-ui-surface px-2.5 py-2">
          <FieldRow
            label={t("invoice.carrier")}
            value={data?.shipping_company?.shipping_company?.name}
          />
          <FieldRow label={t("invoice.service")} value={data?.service_type} />
          <FieldRow label={t("invoice.type")} value={data?.shipment_type} />
          <FieldRow
            label={t("invoice.account")}
            value={data?.shipping_company?.account_name ?? t("invoice.defaultAccount")}
          />
          <FieldRow label={t("invoice.payment")} value={paymentLabel} />
          <FieldRow label={t("invoice.pickupRef")} value={data?.PickupID} />
          <FieldRow label={t("invoice.pickupDetails")} value={pickupDetails} />
        </div>
      </div>
    </Panel>
  );
};

/* ── Payment slip ──────────────────────────────────────────────────────── */

export const PaymentSlipPanel = ({ data }) => {
  const { t } = useTranslation("admin");
  return (
  <div className="min-w-0 overflow-hidden rounded-[7px] border border-dashed border-ui-border-strong bg-ui-surface">
    <div className="flex flex-wrap items-center gap-1.5 border-b border-dashed border-ui-border px-2.5 py-1.5">
      <span className="font-jakarta text-[11px] font-bold text-ui-text">
        {t("invoice.returnThisSlipWithYour")}
      </span>
    </div>
    <div className="grid gap-px bg-ui-border [grid-template-columns:repeat(auto-fit,minmax(132px,1fr))]">
      {[
        [t("invoice.customerId"), data?.created_by?.id || data?.user?.id || "-"],
        [t("invoice.invoiceNumber"), data?.invoice_id ?? "-"],
        [t("invoice.amountDue"), data?.Total != null ? `${data.Total}$` : "-"],
        [t("invoice.dueDate"), "-"],
      ].map(([label, value]) => (
        <div key={label} className="bg-ui-surface px-2.5 py-2">
          <Eyebrow size="sm">{label}</Eyebrow>
          <div className="mt-1 font-manrope text-[13px] font-bold tabular-nums text-ui-text">
            {value}
          </div>
        </div>
      ))}
    </div>
    <div className="border-t border-dashed border-ui-border bg-ui-surface-sunken px-2.5 py-2 font-manrope text-[10px] font-medium leading-relaxed text-ui-text-muted">
      <Trans
        t={t}
        i18nKey="invoice.paymentTerms"
        components={{ b: <span className="font-bold" /> }}
      />
    </div>
  </div>
);
};
